
import { PredictionChartData } from './types';
import { addDays, subDays, format } from 'date-fns';

function generateTimeSeries(
  startPrice: number, 
  scenarios: { high: number; average: number; low: number },
  days: number, 
  volatility: number = 0.02
): Array<{
  date: string;
  timestamp: number;
  historical?: number;
  predictedHigh?: number;
  predictedAverage?: number;
  predictedLow?: number;
  high?: number;
  low?: number;
}> {
  const data = [];
  const today = new Date();
  const historicalDays = Math.floor(days * 0.4); // Cambiato da 0.6 a 0.4 (40% storico, 60% previsione)
  
  // Genera dati storici
  for (let i = historicalDays; i >= 0; i--) {
    const date = subDays(today, i);
    const progress = (historicalDays - i) / historicalDays;
    const basePrice = startPrice + (scenarios.average - startPrice) * progress * 0.4; // Solo parte del movimento
    
    // Aggiungi volatilità realistica
    const randomFactor = (Math.random() - 0.5) * volatility;
    const price = basePrice * (1 + randomFactor);
    
    data.push({
      date: format(date, 'yyyy-MM-dd'),
      timestamp: date.getTime(),
      historical: Math.round(price * 100) / 100
    });
  }
  
  // Ottieni l'ultimo prezzo storico come punto di partenza per le previsioni
  const lastHistoricalPrice = data[data.length - 1].historical!;
  
  // Genera previsioni future con progressione cubica e divergenza estrema
  const futureDays = days - historicalDays;
  
  for (let i = 1; i <= futureDays; i++) {
    const date = addDays(today, i);
    const linearProgress = i / futureDays;
    
    // Usa progressione cubica per divergenza massima
    const cubicProgress = Math.pow(linearProgress, 3); // Ancora più aggressivo
    
    // Calcola le tre linee di previsione con divergenza massima
    const highDifference = scenarios.high - lastHistoricalPrice;
    const averageDifference = scenarios.average - lastHistoricalPrice;
    const lowDifference = scenarios.low - lastHistoricalPrice;
    
    // Applica la progressione cubica con volatilità estrema e bias fortissimi
    const highPrice = lastHistoricalPrice + (highDifference * (cubicProgress + linearProgress * 0.5)) + 
                     (Math.random() - 0.1) * volatility * 120 * linearProgress; // Bias estremo verso l'alto
    
    const averagePrice = lastHistoricalPrice + (averageDifference * linearProgress) + 
                        (Math.random() - 0.5) * volatility * 50 * linearProgress; // Volatilità neutra
    
    const lowPrice = lastHistoricalPrice + (lowDifference * (cubicProgress + linearProgress * 0.5)) + 
                    (Math.random() - 0.9) * volatility * 120 * linearProgress; // Bias estremo verso il basso
    
    data.push({
      date: format(date, 'yyyy-MM-dd'),
      timestamp: date.getTime(),
      predictedHigh: Math.round(Math.max(highPrice, averagePrice + 2.0) * 100) / 100, // Distanza minima molto maggiore
      predictedAverage: Math.round(averagePrice * 100) / 100,
      predictedLow: Math.round(Math.min(lowPrice, averagePrice - 2.0) * 100) / 100 // Distanza minima molto maggiore
    });
  }
  
  return data;
}

export const mockGoldChartData: PredictionChartData = {
  metal: 'ORO',
  currentPrice: 65.50,
  targetPrice: 69.90,
  targetPercentage: 6.7,
  timeframe: '30 giorni',
  confidence: 78,
  timeSeries: generateTimeSeries(
    63.20, 
    { high: 82.50, average: 69.90, low: 55.20 }, // Divergenza massima: range di quasi 30€
    45, 
    0.055 // Volatilità estrema
  ),
  scenarios: {
    high: { price: 82.50, percentage: 26.0, probability: 25 },
    average: { price: 69.90, percentage: 6.7, probability: 50 },
    low: { price: 55.20, percentage: -15.7, probability: 25 }
  }
};

export const mockSilverChartData: PredictionChartData = {
  metal: 'ARGENTO',
  currentPrice: 0.82,
  targetPrice: 0.91,
  targetPercentage: 11.0,
  timeframe: '30 giorni',
  confidence: 72,
  timeSeries: generateTimeSeries(
    0.78, 
    { high: 1.25, average: 0.91, low: 0.58 }, // Divergenza massima: range di 0.67€
    45, 
    0.065 // Volatilità estrema per l'argento
  ),
  scenarios: {
    high: { price: 1.25, percentage: 52.4, probability: 20 },
    average: { price: 0.91, percentage: 11.0, probability: 60 },
    low: { price: 0.58, percentage: -29.3, probability: 20 }
  }
};
